import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGraduationCap,
  faBookOpen,
  faFilm,
  faComments,
  faEnvelope,
  faPlus,
  faMinus,
  faCheck,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";
import usePageTitle from "../hooks/usePageTitle";
import { signupAcademy } from "../api/academy";

// Every colour that differs between themes goes through the --fma-* variables
// defined under .kk-academy in styles/tailwind.css, so the hex-keyed light-mode
// override sheet never touches this page and light mode is one variable block.
// The few literal colours left sit on surfaces that stay dark in both themes.
// Unsplash photo 1758232589439-f5ec09dc92c2, self-hosted in client/public/images.
const HERO_IMAGE = "/images/academy-hero.webp";
const HERO_SRCSET = "/images/academy-hero-800.webp 800w, /images/academy-hero.webp 1600w";
const NOISE_SVG =
  "<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 1 0'/></filter><rect width='100%' height='100%' filter='url(#n)'/></svg>";
// Punches the two half-circle notches that make the hero button read as a ticket.
const notch = (edge) =>
  `radial-gradient(circle 9px at calc(100% - 128px) ${edge === "top" ? "0" : "100%"},#0000 98%,#000) ${edge}/100% 51% no-repeat`;
const TICKET_MASK = `${notch("top")},${notch("bottom")}`;

const JOSEFIN = "tw-font-['Josefin_Sans',sans-serif] tw-font-bold";
const SECTION = "tw-flex tw-min-h-[100svh] tw-flex-col tw-justify-center";
const EYEBROW = "tw-text-[13px] tw-font-bold tw-uppercase tw-tracking-[0.1em] tw-text-[color:var(--fma-hl)]";
const CARD = "tw-rounded-2xl tw-border tw-border-solid tw-border-[color:var(--fma-border)] tw-bg-[color:var(--fma-card)]";
const MUTED = "tw-m-0 tw-text-[color:var(--fma-muted)]";
const PINK_BTN =
  "tw-flex tw-items-center tw-justify-center tw-rounded-xl tw-bg-[color:var(--fma-pink)] tw-font-bold tw-uppercase tw-tracking-[0.1em] tw-text-[color:var(--fma-ink)] tw-no-underline tw-shadow-[0_0_15px_rgba(255,67,167,0.4)] tw-transition-colors hover:tw-bg-[color:var(--fma-pink-hover)] hover:tw-text-[color:var(--fma-ink)]";
const INPUT =
  "tw-min-h-[54px] tw-rounded-xl tw-border tw-border-solid tw-border-[color:var(--fma-border)] tw-bg-[color:var(--fma-subtle)] tw-px-4 tw-text-[17px] tw-text-[color:var(--fma-text)] tw-outline-none placeholder:tw-text-[color:var(--fma-cap)] focus:tw-border-[color:var(--fma-pink)] focus:tw-ring-0 focus:tw-shadow-[0_0_0_3px_rgba(255,67,167,0.3)]";
const FIELD_LABEL = "tw-text-xs tw-font-bold tw-uppercase tw-tracking-[0.1em] tw-text-[color:var(--fma-cap)]";

const ROLES = ["Filmmaker", "Actor", "Other"];

function AcademyPage() {
  const { t } = useTranslation();
  usePageTitle(t("Film Marketing Academy"));

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState(ROLES[0]);
  const [website, setWebsite] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const [showSticky, setShowSticky] = useState(false);
  const formRef = useRef(null);

  const sessionDate = t("Sat, Oct 17");
  const sessionTime = t("12 PM ET");

  useEffect(() => {
    const onScroll = () => {
      const r = formRef.current?.getBoundingClientRect();
      const formVisible = !!r && r.top < window.innerHeight && r.bottom > 0;
      setShowSticky(window.scrollY > 640 && !formVisible);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const toRegister = (e) => {
    e.preventDefault();
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    formRef.current?.scrollIntoView({ behavior: reduce ? "auto" : "smooth" });
  };

  // Radio group keyboard pattern: one tab stop, arrows move and select.
  const onRoleKey = (e) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
    if (!step) return;
    e.preventDefault();
    const next = (ROLES.indexOf(role) + step + ROLES.length) % ROLES.length;
    setRole(ROLES[next]);
    e.currentTarget.querySelectorAll('[role="radio"]')[next].focus();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return setError(t("Please add your name."));
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return setError(t("That email doesn’t look right."));
    setSending(true);
    setError("");
    try {
      await signupAcademy({ name: name.trim(), email: email.trim(), role, website });
      setSubmitted(true);
    } catch (err) {
      setError(
        err.response?.status === 429
          ? t("Too many signups from this connection. Please try again in an hour.")
          : t("Something went wrong. Please try again.")
      );
    } finally {
      setSending(false);
    }
  };

  const benefits = [
    { icon: faGraduationCap, title: t("Build a marketing plan from scratch"), body: t("Join the Film Marketing Academy to learn how to create a film marketing plan from scratch, and raise funds for your film, for free.") },
    { icon: faBookOpen, title: t("Free Marketing Plan Blueprint"), body: t("Receive a digital copy of the Free Marketing Plan Blueprint e-book.") },
    { icon: faFilm, title: t("KinoKlik software, free"), body: t("Get free access to the KinoKlik film marketing software."), link: true },
    { icon: faComments, title: t("Private Academy Discord"), body: t("Learn and grow as a film marketing professional alongside a community of like-minded filmmakers.") },
    { icon: faEnvelope, title: t("For Filmmakers newsletter"), body: t("Immediate access to the For Filmmakers Marketing newsletter.") },
  ];

  const steps = [
    { title: t("Position"), body: t("Sharpen your logline, hook and comparable titles so anyone gets your film in ten seconds.") },
    { title: t("Package"), body: t("Assemble an EPK with stills, trailer, synopsis, credits and press that makes saying yes easy.") },
    { title: t("Audience"), body: t("Map your core viewers and communities, and start building a following before the premiere.") },
    { title: t("Festivals"), body: t("Build a tiered submission strategy that protects your premiere status and your budget.") },
    { title: t("Distribution"), body: t("Pitch distributors and sales agents, or plan a self-release, with a clear ask and timeline.") },
  ];

  const faqs = [
    { q: t("Is it really free?"), a: t("Yes. Membership, the live workshops, the Discord and the EPK template are all free. No credit card needed.") },
    { q: t("What happens after I sign up?"), a: t("You get an email with your Discord invite, a second one to confirm your newsletter subscription, and the link for each live session before it starts.") },
    { q: t("Can I watch sessions later?"), a: t("Yes. Replays are shared with members after each session. Questions are only answered live.") },
    { q: t("Who is it for?"), a: t("Independent filmmakers with a finished film or one in post: shorts, features and documentaries. Actors and producers building their profile are welcome too.") },
    { q: t("What is KinoKlik?"), a: t("KinoKlik hosts Electronic Press Kits and filmmaker and actor profiles, so festivals, distributors and press find everything about your film in one link.") },
  ];

  return (
    // tw-isolate keeps the grain's z-index local to the page, so navbar menus stay above it.
    <div className="kk-academy tw-relative tw-isolate tw-overflow-x-hidden tw-bg-[image:var(--fma-pagebg)] tw-font-roboto tw-text-[17px] tw-leading-[1.55] tw-text-[color:var(--fma-text)]">
      <div
        aria-hidden="true"
        className="kk-academy-grain tw-pointer-events-none tw-fixed tw-inset-[-50%] tw-z-[60]"
        style={{ backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(NOISE_SVG)}")` }}
      />

      <header className="tw-relative tw-flex tw-min-h-[calc(100svh-5rem)] tw-flex-col tw-overflow-hidden tw-bg-[color:var(--fma-night)] tw-text-[color:var(--fma-white)]">
        <img
          src={HERO_IMAGE}
          srcSet={HERO_SRCSET}
          sizes="100vw"
          alt={t("A cinema entrance at night with an illuminated marquee")}
          className="tw-absolute tw-inset-0 tw-h-full tw-w-full tw-object-cover tw-object-[center_40%]"
        />
        <div className="tw-absolute tw-inset-0 tw-bg-[linear-gradient(90deg,rgba(9,1,15,0.92)_0%,rgba(9,1,15,0.55)_50%,rgba(9,1,15,0.1)_100%),linear-gradient(0deg,rgba(9,1,15,0.95)_0%,rgba(30,0,57,0)_60%)]" />
        <div className="tw-relative tw-mx-auto tw-flex tw-w-full tw-max-w-[1160px] tw-flex-1 tw-flex-col tw-justify-end tw-gap-[clamp(14px,2.8svh,26px)] tw-px-5 tw-pb-[clamp(32px,8svh,88px)] tw-pt-[clamp(24px,6svh,64px)]">
          <div className="tw-flex tw-items-center tw-gap-2 tw-self-start tw-rounded-full tw-border tw-border-solid tw-border-[rgba(255,255,255,0.15)] tw-bg-[rgba(9,1,15,0.45)] tw-px-3.5 tw-py-1.5 tw-text-xs tw-font-bold tw-uppercase tw-tracking-[0.1em] tw-text-[color:var(--fma-pink-soft)]">
            <span className="tw-h-2 tw-w-2 tw-rounded-full tw-bg-[color:var(--fma-pink-soft)]" />
            <span>{t("Film Marketing Academy")}</span>
          </div>
          <h1 className={`${JOSEFIN} tw-m-0 tw-max-w-[15ch] tw-text-[length:clamp(36px,min(8vw,10svh),96px)] tw-uppercase tw-leading-[0.95] tw-tracking-[0.01em] tw-text-[color:var(--fma-white)]`}>
            {t("Learn how to promote your film online,")}{" "}
            <span className="tw-bg-[linear-gradient(90deg,#FFB0CF,#DDB7FF)] tw-bg-clip-text tw-text-transparent">
              {t("with zero dollars.")}
            </span>
          </h1>
          <p className="tw-m-0 tw-max-w-[44ch] tw-text-[length:clamp(16px,min(4vw,2.8svh),21px)] tw-font-medium tw-leading-[1.45]">
            {t("Register for the free Film Marketing Academy and learn to promote your film online using KinoKlik film EPK software.")}
          </p>
          <a
            href="#register"
            onClick={toRegister}
            aria-label={t("Register now, free")}
            className="tw-flex tw-min-h-[76px] tw-w-full tw-max-w-[420px] tw-self-start tw-text-[color:var(--fma-ink)] tw-no-underline tw-drop-shadow-[0_0_16px_rgba(255,67,167,0.45)] tw-transition-transform hover:tw--translate-y-0.5 hover:tw-text-[color:var(--fma-ink)]"
            style={{ WebkitMask: TICKET_MASK, mask: TICKET_MASK }}
          >
            <span className="tw-flex tw-flex-1 tw-flex-col tw-justify-center tw-gap-0.5 tw-rounded-l-xl tw-bg-[color:var(--fma-pink)] tw-px-5 tw-py-3">
              <span className="tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-[0.2em]">{t("Admit one · Free")}</span>
              <span className={`${JOSEFIN} tw-text-[22px] tw-uppercase tw-leading-[1.1] tw-tracking-[0.08em]`}>{t("Register now")}</span>
            </span>
            <span className="tw-flex tw-w-32 tw-flex-none tw-flex-col tw-justify-center tw-gap-0.5 tw-rounded-r-xl tw-border-0 tw-border-l-2 tw-border-dashed tw-border-[rgba(87,0,51,0.35)] tw-bg-[color:var(--fma-pink-soft)] tw-px-4 tw-py-3">
              <span className="tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-[0.2em]">{t("Next session")}</span>
              <span className="tw-text-[15px] tw-font-bold tw-leading-[1.2]">{sessionDate}</span>
              <span className="tw-text-[13px] tw-font-medium">{sessionTime}</span>
            </span>
          </a>
        </div>
      </header>

      <section className={`${SECTION} tw-border-0 tw-border-b tw-border-solid tw-border-[color:var(--fma-border)]`}>
        <div className="tw-mx-auto tw-flex tw-w-full tw-max-w-[820px] tw-flex-col tw-gap-[18px] tw-px-5 tw-py-[clamp(32px,6svh,64px)]">
          <div className={EYEBROW}>{t("About the webinar")}</div>
          <h2 className={`${JOSEFIN} tw-m-0 tw-text-[length:clamp(30px,7vw,46px)] tw-leading-[1.05] tw-tracking-[0.02em]`}>
            {t("Build an emotional bridge between your film and your audience.")}
          </h2>
          <p className={`${MUTED} tw-text-lg`}>
            {t("In this free marketing webinar, we explore every element of a film marketing plan: defining your film’s objectives, redefining your film’s audience, finding that audience online (or offline), building your own audience, running a film marketing SWOT analysis, and much more.")}
          </p>
          <p className={`${MUTED} tw-text-lg`}>
            {t("We’ll also review in detail how to create a proper film EPK to present your film to festivals, distributors and press.")}
          </p>
          <p className="tw-m-0 tw-text-lg tw-font-semibold">
            {t("By the end of the workshop, you’ll be able to create your own film marketing plan like a pro, for free.")}
          </p>
        </div>
      </section>

      <section className={SECTION}>
        <div className="tw-mx-auto tw-flex tw-w-full tw-max-w-[1160px] tw-flex-col tw-gap-[clamp(16px,3svh,28px)] tw-px-5 tw-py-[clamp(32px,6svh,64px)]">
          <div className={EYEBROW}>{t("What you will receive")}</div>
          <div className="tw-grid tw-grid-cols-[repeat(auto-fit,minmax(min(100%,200px),1fr))] tw-gap-4">
            {benefits.map((b, i) => (
              <div key={b.title} className={`${CARD} tw-flex tw-flex-col tw-gap-2.5 tw-px-[18px] tw-py-[clamp(16px,2.6svh,24px)]`}>
                <div className="tw-flex tw-items-center tw-justify-between">
                  <span className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-bg-[rgba(255,67,167,0.2)] tw-text-[color:var(--fma-pink)]">
                    <FontAwesomeIcon icon={b.icon} />
                  </span>
                  <span className={`${JOSEFIN} tw-text-2xl tw-text-[color:var(--fma-border)]`}>{`0${i + 1}`}</span>
                </div>
                <h3 className={`${JOSEFIN} tw-m-0 tw-text-[21px] tw-leading-[1.15] tw-tracking-[0.02em]`}>{b.title}</h3>
                <p className={`${MUTED} tw-text-[15px] tw-leading-normal`}>{b.body}</p>
                {b.link && (
                  <Link to="/" className="tw-font-bold tw-text-[color:var(--fma-link)] tw-no-underline hover:tw-text-[color:var(--fma-link)] hover:tw-underline">
                    www.KinoKlik.ca
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={`${SECTION} tw-border-0 tw-border-y tw-border-solid tw-border-[color:var(--fma-border)] tw-bg-[color:var(--fma-bg2)]`}>
        <div className="tw-mx-auto tw-grid tw-w-full tw-max-w-[1160px] tw-grid-cols-[repeat(auto-fit,minmax(min(100%,380px),1fr))] tw-items-center tw-gap-[clamp(24px,4svh,48px)] tw-px-5 tw-py-[clamp(32px,6svh,72px)]">
          <div className="tw-flex tw-flex-col tw-gap-[clamp(10px,2svh,18px)]">
            <div className={EYEBROW}>{t("The curriculum")}</div>
            <h2 className={`${JOSEFIN} tw-m-0 tw-text-[length:clamp(30px,min(8vw,7svh),64px)] tw-uppercase tw-leading-none tw-tracking-[0.02em]`}>
              {t("The 5-Step Release Plan")}
            </h2>
            <p className={`${MUTED} tw-max-w-[44ch]`}>
              {t("Every Academy workshop builds on one framework for taking a finished film from hard drive to screen, whether you’re chasing festivals, a distributor or a self-release.")}
            </p>
            <div className="tw-mt-1 tw-flex tw-flex-col tw-gap-2.5 tw-rounded-2xl tw-border tw-border-solid tw-border-[rgba(255,176,207,0.35)] tw-bg-[image:var(--fma-bonus-bg)] tw-px-5 tw-py-[clamp(16px,2.6svh,24px)] tw-text-[color:var(--fma-white)]">
              <span className="tw-self-start tw-rounded-full tw-bg-[color:var(--fma-pink-soft)] tw-px-3 tw-py-[5px] tw-text-xs tw-font-bold tw-uppercase tw-tracking-[0.1em] tw-text-[color:var(--fma-ink)]">
                {t("Bonus")}
              </span>
              <h3 className={`${JOSEFIN} tw-m-0 tw-text-[22px] tw-leading-[1.1] tw-tracking-[0.02em] tw-text-[color:var(--fma-white)]`}>
                {t("Free EPK template on KinoKlik")}
              </h3>
              <p className="tw-m-0 tw-text-[15px] tw-text-[color:var(--fma-rose)]">
                {t("Every member gets a ready-made Electronic Press Kit on KinoKlik: logline, stills, trailer, credits and press, in one link you can send to any programmer.")}
              </p>
              <a
                href="#register"
                onClick={toRegister}
                className="tw-flex tw-min-h-[52px] tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-[rgba(255,255,255,0.2)] tw-bg-[rgba(255,255,255,0.05)] tw-px-5 tw-py-3 tw-text-center tw-text-[15px] tw-font-bold tw-uppercase tw-tracking-[0.1em] tw-text-[color:var(--fma-white)] tw-no-underline tw-transition-colors hover:tw-bg-[rgba(113,44,176,0.35)] hover:tw-text-[color:var(--fma-white)]"
              >
                {t("Join to claim it")}
              </a>
            </div>
          </div>
          <ol className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">
            {steps.map((s, i) => (
              <li key={s.title} className={`${CARD} tw-grid tw-grid-cols-[36px_1fr] tw-items-start tw-gap-3.5 tw-px-4 tw-py-[clamp(10px,1.8svh,16px)]`}>
                <span className={`${JOSEFIN} tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-[color:var(--fma-hl)] tw-pt-[3px] tw-text-[17px] tw-text-[color:var(--fma-hl)]`}>
                  {i + 1}
                </span>
                <div className="tw-flex tw-flex-col tw-gap-0.5">
                  <h3 className="tw-m-0 tw-text-base tw-font-bold tw-uppercase tw-tracking-[0.08em]">{s.title}</h3>
                  <p className={`${MUTED} tw-text-[15px] tw-leading-[1.45]`}>{s.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className={SECTION}>
        <div className="tw-mx-auto tw-grid tw-w-full tw-max-w-[1160px] tw-grid-cols-[repeat(auto-fit,minmax(min(100%,340px),1fr))] tw-items-center tw-gap-10 tw-px-5 tw-py-[clamp(32px,6svh,72px)]">
          <div className="tw-relative tw-mx-auto tw-aspect-[4/5] tw-max-h-[80svh] tw-w-full tw-max-w-[440px] tw-overflow-hidden tw-rounded-2xl tw-border tw-border-solid tw-border-[color:var(--fma-border)] tw-bg-[image:var(--fma-stripes)]">
            <img
              src="/images/academy-host.webp"
              alt={t("Portrait of Alberto Tihan")}
              loading="lazy"
              className="tw-absolute tw-inset-0 tw-h-full tw-w-full tw-object-cover"
            />
          </div>
          <div className="tw-flex tw-flex-col tw-gap-[18px]">
            <div className={EYEBROW}>{t("Led by")}</div>
            <h2 className={`${JOSEFIN} tw-m-0 tw-text-[length:clamp(30px,min(8vw,7svh),64px)] tw-uppercase tw-leading-none tw-tracking-[0.02em]`}>
              Alberto Tihan
            </h2>
            <p className={`${MUTED} tw-max-w-[50ch]`}>
              {t("Alberto Tihan graduated from Humber College in Acting for Film & Television and studied Business Communications at York University in Toronto.")}
            </p>
            <p className={`${MUTED} tw-max-w-[50ch]`}>
              {t("With over 10 years of experience in the indie film industry, and having observed the challenges of promoting and marketing a film project, Alberto created the KinoKlik EPK marketing software to help independent filmmakers promote, fundraise for and manage their film projects.")}
            </p>
            <p className={`${MUTED} tw-max-w-[50ch]`}>
              {t("KinoKlik strives to help indie filmmakers promote their film to industry professionals, grow the film’s audience and raise more funding to maximize the film’s audience, fans, views and sales.")}
            </p>
          </div>
        </div>
      </section>

      <section className={`${SECTION} tw-bg-[image:var(--fma-cta-bg)] tw-text-[color:var(--fma-white)]`}>
        <div className="tw-mx-auto tw-flex tw-w-full tw-max-w-[820px] tw-flex-col tw-items-center tw-gap-[22px] tw-px-5 tw-py-[clamp(40px,8svh,88px)] tw-text-center">
          <div className="tw-text-[13px] tw-font-bold tw-uppercase tw-tracking-[0.1em] tw-text-[color:var(--fma-pink-soft)]">
            {t("Next live session")} · {sessionDate} · {sessionTime}
          </div>
          <h2 className={`${JOSEFIN} tw-m-0 tw-text-[length:clamp(32px,min(9vw,9svh),76px)] tw-uppercase tw-leading-none tw-tracking-[0.02em] tw-text-[color:var(--fma-white)]`}>
            {t("Your film deserves an audience.")}
          </h2>
          <p className="tw-m-0 tw-max-w-[42ch] tw-text-[color:var(--fma-rose)]">
            {t("Join free and get the next live workshop, the Discord community and every event after that.")}
          </p>
          <a href="#register" onClick={toRegister} className={`${PINK_BTN} tw-min-h-[60px] tw-w-full tw-max-w-[440px] tw-px-7 tw-py-3 tw-text-[19px]`}>
            {t("Register now")}
          </a>
        </div>
      </section>

      <section className={SECTION}>
        <div className="tw-mx-auto tw-flex tw-w-full tw-max-w-[820px] tw-flex-col tw-gap-6 tw-px-5 tw-py-[clamp(32px,6svh,72px)]">
          <div className={EYEBROW}>{t("Questions")}</div>
          <div className="tw-flex tw-flex-col tw-gap-2.5">
            {faqs.map((f, i) => (
              <div key={f.q} className={`${CARD} tw-overflow-hidden`}>
                <button
                  type="button"
                  aria-expanded={openFaq === i}
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="tw-flex tw-min-h-[64px] tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-gap-4 tw-border-0 tw-bg-transparent tw-px-5 tw-py-4 tw-text-left tw-font-roboto tw-text-lg tw-font-semibold tw-text-[color:var(--fma-text)]"
                >
                  <span>{f.q}</span>
                  <FontAwesomeIcon icon={openFaq === i ? faMinus : faPlus} className="tw-flex-shrink-0 tw-text-[color:var(--fma-hl)]" />
                </button>
                {openFaq === i && <p className={`${MUTED} tw-px-5 tw-pb-5`}>{f.a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        id="register"
        ref={formRef}
        className={`${SECTION} tw-border-0 tw-border-t tw-border-solid tw-border-[color:var(--fma-border)] tw-bg-[color:var(--fma-bg2)]`}
      >
        <div className="tw-mx-auto tw-w-full tw-max-w-[600px] tw-px-5 tw-py-[clamp(32px,6svh,72px)]">
          <div className={`${CARD} tw-flex tw-flex-col tw-gap-[26px] tw-px-6 tw-py-8 tw-shadow-[0_20px_40px_rgba(0,0,0,0.25)]`}>
            <div className="tw-flex tw-flex-col tw-gap-2.5">
              <div className={EYEBROW}>{t("Free membership")}</div>
              <h2 className={`${JOSEFIN} tw-m-0 tw-text-[length:clamp(34px,8vw,52px)] tw-uppercase tw-leading-none tw-tracking-[0.02em]`}>
                {t("Join the Academy")}
              </h2>
            </div>
            {submitted ? (
              <div className="tw-flex tw-flex-col tw-gap-3.5" role="status">
                <span className="tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-full tw-bg-[rgba(255,67,167,0.2)] tw-text-[color:var(--fma-pink)]">
                  <FontAwesomeIcon icon={faCheck} />
                </span>
                <span className={`${JOSEFIN} tw-text-4xl tw-leading-[1.05] tw-tracking-[0.02em]`}>
                  {t("You’re in, {{name}}.", { name: name.trim().split(/\s+/)[0] })}
                </span>
                <p className={MUTED}>
                  {t("Your Discord invite is on its way to")} <strong className="tw-text-[color:var(--fma-text)]">{email.trim()}</strong>.{" "}
                  {t("Next live session")}: {sessionDate}, {sessionTime}.
                </p>
                <p className={MUTED}>{t("While you wait, set up your free KinoKlik profile. We’ll use it in the workshops.")}</p>
                <Link
                  to="/signup"
                  className="tw-flex tw-min-h-[52px] tw-items-center tw-justify-center tw-gap-2 tw-rounded-xl tw-border tw-border-solid tw-border-[color:var(--fma-border)] tw-bg-[color:var(--fma-subtle)] tw-font-semibold tw-text-[color:var(--fma-text)] tw-no-underline tw-transition-colors hover:tw-bg-[rgba(113,44,176,0.2)] hover:tw-text-[color:var(--fma-text)]"
                >
                  {t("Create my KinoKlik profile")}
                  <FontAwesomeIcon icon={faArrowRight} />
                </Link>
              </div>
            ) : (
              <form onSubmit={submit} noValidate className="tw-flex tw-flex-col tw-gap-[18px]">
                <label className="tw-m-0 tw-flex tw-flex-col tw-gap-2">
                  <span className={FIELD_LABEL}>{t("Name")}</span>
                  <input
                    value={name}
                    onChange={(e) => { setName(e.target.value); setError(""); }}
                    autoComplete="name"
                    maxLength={100}
                    placeholder={t("Your name")}
                    className={INPUT}
                  />
                </label>
                <label className="tw-m-0 tw-flex tw-flex-col tw-gap-2">
                  <span className={FIELD_LABEL}>{t("Email")}</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(""); }}
                    autoComplete="email"
                    inputMode="email"
                    maxLength={254}
                    placeholder="you@example.com"
                    className={INPUT}
                  />
                </label>
                {/* Honeypot: hidden from people, filled by bots, dropped by the server. */}
                <input
                  type="text"
                  name="website"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="tw-hidden"
                />
                <div className="tw-flex tw-flex-col tw-gap-2">
                  <span id="academy-role-label" className={FIELD_LABEL}>{t("I am a")}</span>
                  <div
                    role="radiogroup"
                    aria-labelledby="academy-role-label"
                    onKeyDown={onRoleKey}
                    className="tw-grid tw-grid-cols-[repeat(auto-fit,minmax(110px,1fr))] tw-gap-2"
                  >
                    {ROLES.map((r) => (
                      <button
                        key={r}
                        type="button"
                        role="radio"
                        aria-checked={role === r}
                        tabIndex={role === r ? 0 : -1}
                        onClick={() => setRole(r)}
                        className={`tw-min-h-[50px] tw-cursor-pointer tw-rounded-xl tw-border tw-border-solid tw-px-3 tw-py-2 tw-font-roboto tw-text-base tw-transition-colors ${
                          role === r
                            ? "tw-border-[color:var(--fma-hl)] tw-bg-[rgba(113,44,176,0.3)] tw-font-bold tw-text-[color:var(--fma-text)]"
                            : "tw-border-[color:var(--fma-border)] tw-bg-[color:var(--fma-subtle)] tw-font-medium tw-text-[color:var(--fma-muted)] hover:tw-bg-[rgba(113,44,176,0.2)]"
                        }`}
                      >
                        {t(r)}
                      </button>
                    ))}
                  </div>
                </div>
                {error && <p role="alert" className="tw-m-0 tw-text-[15px] tw-font-medium tw-text-[color:var(--fma-error)]">{error}</p>}
                <button
                  type="submit"
                  disabled={sending}
                  className={`${PINK_BTN} tw-mt-1.5 tw-min-h-[60px] tw-cursor-pointer tw-border-0 tw-px-6 tw-py-3 tw-font-roboto tw-text-[19px] disabled:tw-cursor-wait disabled:tw-opacity-70`}
                >
                  {sending ? t("Sending…") : t("Send my invite")}
                </button>
                <p className="tw-m-0 tw-text-center tw-text-[13px] tw-text-[color:var(--fma-cap)]">
                  {t("Free. We’ll email your Discord invite, newsletter and event links. Unsubscribe anytime.")}
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {showSticky && !submitted && (
        <div className="tw-fixed tw-inset-x-0 tw-bottom-0 tw-z-50 tw-border-0 tw-border-t tw-border-solid tw-border-[rgba(255,255,255,0.1)] tw-bg-[rgba(30,0,57,0.92)] tw-px-3 tw-pb-[calc(10px+env(safe-area-inset-bottom))] tw-pt-2.5 tw-text-[color:var(--fma-white)] tw-backdrop-blur-[10px]">
          <div className="tw-mx-auto tw-flex tw-max-w-[720px] tw-items-center tw-gap-3">
            <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-leading-tight">
              <span className="tw-text-[15px] tw-font-bold">{t("Film Marketing Academy")}</span>
              <span className="tw-text-xs tw-text-[color:var(--fma-rose)]">
                {t("Next session")} {sessionDate} · {sessionTime}
              </span>
            </div>
            <a href="#register" onClick={toRegister} className={`${PINK_BTN} tw-min-h-[48px] tw-flex-shrink-0 tw-px-5 tw-py-2.5 tw-text-[15px]`}>
              {t("Join free")}
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

export default AcademyPage;
